/*
 * @providesModule ModalButton
 *
 */

import React from 'react';
import {View, Text, StyleSheet, ViewStyle, TextStyle, TextProps} from 'react-native';
import {SkillzButton} from '@skillz-rn/atoms';
import ThemeOverrideProps from 'ThemeOverrideProps';

const {fontExtraBold} = ThemeOverrideProps.sheetStyles;

type ButtonVariant = 'primary' | 'secondary';
type ModalButtonProps = {
  /**
   * The variant of the button, either 'primary' or 'secondary'.
   */
  variant?: ButtonVariant;
  /**
   * The text to display on the button.
   */
  text: string;
  /**
   * The function to call when the button is pressed.
   */
  onPress?: () => void;
  /**
   * The style of the button container.
   */
  buttonContainerStyle?: ViewStyle;

  /**
   * The props to pass to the button text.
   */
  buttonTextProps?: TextProps;
};

export function ModalButton({
  text,
  variant = 'primary',
  onPress,
  buttonContainerStyle,
  buttonTextProps,
}: ModalButtonProps) {
  const styles = stylesFn(variant);

  return (
    <SkillzButton
      text={text}
      size={'L'}
      branding='Skillz'
      type='Tertiary'
      containerStyle={{...styles.buttonContainerStyle, ...buttonContainerStyle}}
      style={{
        textStyle: styles.buttonTextStyle,
      }}
      onPress={onPress}
      textProps={buttonTextProps}
    />
  );
}

const stylesFn = (variant: ButtonVariant) => {
  const BUTTON_CONTAINER: Record<ButtonVariant, ViewStyle> = {
    primary: {
      backgroundColor: '#367BCE',
      elevation: 1,
      shadowColor: '#1F4572',
      shadowOpacity: 1,
      shadowRadius: 1,
      shadowOffset: {height: 1, width: 0},
    },
    secondary: {
      backgroundColor: '#DADEE5',
    },
  };

  const BUTTON_TEXT: Record<
    ButtonVariant,
    Omit<TextStyle, 'color'> & {
      color?: string;
    }
  > = {
    primary: {
      color: '#fff',
    },
    secondary: {
      color: '#484A5E',
    },
  };

  return StyleSheet.create({
    buttonContainerStyle: {
      height: 43,
      borderRadius: 58,
      ...BUTTON_CONTAINER[variant],
    },

    buttonTextStyle: {
      textTransform: 'uppercase',
      fontSize: 18,
      letterSpacing: -0.03,
      verticalAlign: 'middle',
      ...fontExtraBold,
      ...BUTTON_TEXT[variant],
    },
  });
};
